import React from "react";

type TagEntry = [string, string];
type TagMap = Map<string, string>;
type KeyCounter = { current: number };

const ELEMENT_NODE = 1;
const TEXT_NODE = 3;

const DROPPED_TAGS = new Set<string>([
  "noembed",
  "plaintext",
  "script",
  "style",
  "textarea",
  "title",
  "xmp",
]);

const DEFAULT_TAG_ENTRIES: TagEntry[] = [
  ["b", "b"],
  ["br", "br"],
  ["em", "em"],
  ["h1", "h1"],
  ["h2", "h2"],
  ["h3", "h3"],
  ["i", "i"],
  ["li", "li"],
  ["ol", "ol"],
  ["p", "p"],
  ["span", "span"],
  ["strong", "strong"],
  ["sub", "sub"],
  ["sup", "sup"],
  ["ul", "ul"],
];
const DEFAULT_TAG_MAP: TagMap = new Map(DEFAULT_TAG_ENTRIES);
const ALLOWED_TARGET_TAGS = new Set<string>(DEFAULT_TAG_ENTRIES.map(([, target]) => target));

const toReactNodes = (node: Node, keyCounter: KeyCounter, tagMap: TagMap): React.ReactNode[] =>
  Array.from(node.childNodes).reduce<React.ReactNode[]>((nodes, child) => {
    let childNodes: React.ReactNode[];

    if (child.nodeType === TEXT_NODE) {
      childNodes = [(child as Text).data];
    } else if (child.nodeType !== ELEMENT_NODE || DROPPED_TAGS.has((child as Element).localName)) {
      childNodes = [];
    } else if (tagMap.has((child as Element).localName)) {
      const tag = tagMap.get((child as Element).localName) as string;
      const key = `node-${keyCounter.current}`;

      keyCounter.current += 1;

      const children = toReactNodes(child, keyCounter, tagMap);

      childNodes = [
        children.length
          ? React.createElement(tag, { key }, children)
          : React.createElement(tag, { key }),
      ];
    } else {
      childNodes = toReactNodes(child, keyCounter, tagMap);
    }

    return nodes.concat(childNodes);
  }, []);

/**
 * Returns a fresh allowlist Map seeded with the default safe formatting tags.
 * Override targets must be one of those safe tags; any other target throws, so
 * a caller cannot remap a source tag onto a dangerous element.
 */
export const createTagMap = (overrides: TagEntry[] = []): TagMap => {
  const invalidTargets = overrides.filter(([, target]) => !ALLOWED_TARGET_TAGS.has(target));

  if (invalidTargets.length > 0) {
    throw new Error(
      `createTagMap: invalid target tag(s): ${invalidTargets
        .map(([, target]) => target)
        .join(", ")}`
    );
  }

  return new Map([...DEFAULT_TAG_ENTRIES, ...overrides]);
};

/**
 * Sanitizes an untrusted HTML string into safe React node(s) using an allowlist.
 * Allowlisted formatting tags are rebuilt as attribute-less React elements;
 * everything else is dropped or unwrapped to its text. Requires a DOM and
 * returns an empty string if DOMParser is unavailable.
 *
 * Output is never referentially stable; callers that render often should memoize on htmlValue.
 */
export const sanitizeHtmlToNodes = (
  htmlValue: string | null | undefined,
  tagMap: TagMap = DEFAULT_TAG_MAP
): React.ReactNode => {
  let result: React.ReactNode = "";

  if (typeof DOMParser !== "undefined") {
    const parsedDocument = new DOMParser().parseFromString(htmlValue ?? "", "text/html");
    const nodes = toReactNodes(parsedDocument.body, { current: 0 }, tagMap);

    result = nodes.length === 1 ? nodes[0] : nodes;
  }

  return result;
};
